/**
 * SurveyAppearancePreview — shared mini-mockup of the public survey, used by:
 *   1. The Appearance tab in SurveyEditor (live preview, driven by un-saved state).
 *   2. The public SurveyPublic page (real rendering, driven by API state).
 *
 * Single source of truth for: logo placement, background image with overlay,
 * forced theme. The component never mutates `<html>` — theme is scoped via a
 * wrapping container with `dark` or no class. Public page handles its own
 * `<html>` class side-effect; this component handles the visual frame only.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { cardSurfaceClass } from '../../../components/shared/cardSurface';

// v3.5.11 — Logo size presets (matching GlobalReview.jsx / ReviewShield.jsx).
const LOGO_SIZE_CLASSES = {
    small:      'max-h-16 max-w-[120px]',
    medium:     'max-h-20 max-w-[180px]',
    large:      'max-h-32 max-w-[280px]',
    very_large: 'max-h-44 max-w-[360px]',
};

const SurveyAppearancePreview = ({
    logoUrl,
    logoSize = 'medium',
    backgroundImageUrl,
    displayMode = 'auto', // 'auto' | 'light' | 'dark'
    // P_SURVEY_CARD_STYLE_PREVIEW_1.0 — already-resolved surface style
    // ('frosted' | 'clear' | 'solid'). Callers resolve "inherit" before
    // passing it, exactly like the public page uses effective_card_style.
    cardStyle = 'frosted',
    title = '',
    description = '',
    children,             // optional — replaces the placeholder body
    className = '',
    'data-testid': testId = 'survey-appearance-preview',
}) => {
    // Scope theme via wrapping div instead of mutating <html>.
    // 'auto' → no override (inherits parent / system).
    const themeClass = displayMode === 'dark' ? 'dark' : displayMode === 'light' ? '' : '';
    const themeAttr = displayMode === 'light' ? 'light' : undefined;
    // Same variant split the public page uses for its shell, so the
    // preview and the real card resolve to the identical class string.
    const cardSurface = cardSurfaceClass(cardStyle, displayMode === 'dark' ? 'dark' : 'light');

    return (
        <div
            className={`relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 ${themeClass} ${className}`}
            data-survey-display-mode={displayMode}
            data-testid={testId}
        >
            {/* Inner light/dark surface — forced light when displayMode='light' */}
            <div
                data-theme={themeAttr}
                className={`relative bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 min-h-[420px] ${displayMode === 'light' ? 'bg-gradient-to-b from-slate-50 to-white' : ''}`}
                style={backgroundImageUrl ? {
                    backgroundImage: `url("${backgroundImageUrl}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    // Inside a preview pane, fixed attachment looks broken — use scroll.
                    backgroundAttachment: 'scroll',
                } : undefined}
            >
                {/* Fixed 0.5 opacity dark overlay — only over the background image. */}
                {backgroundImageUrl && (
                    <div
                        className="absolute inset-0 bg-black pointer-events-none"
                        style={{ opacity: 0.5 }}
                        aria-hidden="true"
                        data-testid={`${testId}-overlay`}
                    />
                )}

                <div className="relative px-4 py-6">
                    {logoUrl && (
                        <div className="flex justify-center pb-3" data-testid={`${testId}-logo-wrapper`}>
                            <img
                                src={logoUrl}
                                alt=""
                                loading="lazy"
                                className={`block object-contain ${LOGO_SIZE_CLASSES[logoSize] || LOGO_SIZE_CLASSES.medium}`}
                                data-testid={`${testId}-logo`}
                            />
                        </div>
                    )}

                    <div className="max-w-md mx-auto">
                        <Card className={cardSurface} data-card-style={cardStyle} data-testid={`${testId}-card`}>
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {title || 'Survey'}
                                </CardTitle>
                                {description && (
                                    <p className="text-sm text-muted-foreground">{description}</p>
                                )}
                            </CardHeader>
                            <CardContent>
                                {children || (
                                    <div className="space-y-3 text-sm text-muted-foreground">
                                        <p>How would you rate your experience?</p>
                                        <div className="flex gap-1.5 justify-center">
                                            {[1, 2, 3, 4, 5].map(n => (
                                                <span key={n} className="inline-block w-7 h-7 rounded-full border border-amber-400 text-amber-500 flex items-center justify-center text-xs font-semibold">
                                                    {n}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-center opacity-70">
                                            Preview — actual page shows full survey questions.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyAppearancePreview;
